import {useCallback, useEffect, useRef, useState} from "react";
import CustomerSearchForm, {
    CustomerCategory, CustomerSearchFormHandles,
    CustomerSearchType
} from "../components/customer-management/CustomerSearchForm";
import {useNavigate} from "react-router-dom";
import {customerService} from "../services/customerService";
import CustomerSearchCard from "../components/customer-management/CustomerSearchCard";
import EstimateSchedulingPage from "./EstimateSchedulingPage";
import MessageCustomerModal from "../components/customer-management/MessageCustomersModal";
import LoadingSpinner from "../shared/components/loadingSpinner";


const PAGE_SIZE = 20;
const SCROLL_THRESHOLD_PX = 200;
const CustomerManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchTerms, setSearchTerms] = useState<CustomerSearchType>({});
    const [customers, setCustomers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage]                 = useState(0);
    const [loading, setLoading]           = useState(false);
    const [prefetchedCustomers, setPrefetchedCustomers] = useState<[] | null>(null);
    const [prefetching, setPrefetching] = useState(false);
    const [showMessagingModal, setShowMessagingModal]           = useState(false);
    const [searchReady, setSearchReady] = useState(false);
    const formRef = useRef<CustomerSearchFormHandles>(null);
    function removeSearchTerm (category: string, fieldKey: string) {
        const newTerms = { ...searchTerms };
        if(fieldKey === 'customerStatusName'){
            delete newTerms[category]['customerStatusId'];
        }
        if(fieldKey === 'jobStatusName'){
            delete newTerms[category]['jobStatusId'];
        }
        delete newTerms[category][fieldKey];
        setSearchTerms(newTerms);
        formRef.current?.clearField(`${fieldKey}`);
    }

    function GetSearchTermsHTML() {
        return (
            <>
                {Object.entries(searchTerms).map(([category, fieldsObj]) =>
                    Object.entries(fieldsObj!).map(([fieldKey, fieldValue]) => {
                        if(['customerStatusId', 'jobStatusId'].includes(fieldKey)) return null;
                        if(fieldKey === 'scheduledViaWeb') fieldValue = fieldValue === "true" ? "By Web" : "By Phone"
                        return (
                            <span key={`${category}-${fieldKey}`} className="searchBadge">
                                <span className="me-2">
                                    <span className="icon">
                                        <i className={"fa-regular " + (category === "job" ? "fa-file-invoice" : category === "customer" ? "fa-user" : "fa-house")}/>
                                    </span>
                                    {fieldValue}
                                </span>
                                <a className="badgeClear"
                                   onClick={() => removeSearchTerm(category as CustomerCategory, fieldKey)}>
                                    <i className="fa-regular fa-circle-xmark"/>   
                                </a>
                            </span>
                        )

                    })
                )}
            </>
        );
    }

    function exportCustomers() {
        customerService.ExportCustomers(searchTerms).then(csvText => {
            const blob = new Blob([csvText], {type: "text/csv"});
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = "customers‐export.csv";
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        });
    }


    const prefetchedRef = useRef<[] | null>(null);

    useEffect(() => {
        prefetchedRef.current = prefetchedCustomers;
    }, [prefetchedCustomers]);

    useEffect(() => {
        let cancelled = false;
        const load = async (prefetchedCustomerData) => {
            if(!searchReady) return;
            setLoading(true);
            try {
                if ((page) * PAGE_SIZE > totalCount) return;
                const result = await customerService.searchCustomers(searchTerms, page, PAGE_SIZE);
                if (cancelled) return;
                setTotalCount(result.totalItemsCount);
                setCustomers(prev =>
                    page === 0 ? result.items : [...prev, ...result.items]
                );

                // get next page if needed
                if ((page + 1) * PAGE_SIZE < result.totalItemsCount) {
                    setPrefetching(true);
                    const prefetch = await customerService.searchCustomers(searchTerms, page + 1, PAGE_SIZE);
                    if (!cancelled) setPrefetchedCustomers(prefetch.items);
                    setPrefetching(false);
                } else {
                    setPrefetchedCustomers(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        if (!searchTerms || Object.keys(searchTerms).length === 0 || Object.values(searchTerms).every(inner => Object.keys(inner).length === 0)){
            setCustomers([]);
            return;
        }
        load(prefetchedCustomers);
        return () => { cancelled = true; };
    }, [searchTerms, page]);

    const onSearch = (newTerms: CustomerSearchType) => {
        setSearchTerms(newTerms);
        setPage(0);
    };

    const lastCustomerRef = useRef<HTMLDivElement>(null);
    const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
        const target = entries[0];
        if (target.isIntersecting && !loading && customers.length < totalCount) {
            if (prefetchedCustomers) {
                setCustomers(prev => [...prev, ...prefetchedCustomers]);
                setPrefetchedCustomers(null);
                setPage(prev => prev + 2);
            } else {
                setPage(prev => prev + 1); 
            }
        }
    }, [loading, customers.length, totalCount]);

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '200px',
            threshold: 0.1,
        });
        if (lastCustomerRef.current) observer.observe(lastCustomerRef.current);
        return () => {
            if (lastCustomerRef.current) observer.unobserve(lastCustomerRef.current);
        };
    }, [handleObserver]);

    function GetCustomerCards() {
        return (
            <>
                {customers.map((customer, i) =>
                    i === customers.length - 1 ? (
                        <div ref={lastCustomerRef} key={customer.id}>
                            <CustomerSearchCard customer={customer} />
                        </div>
                    ) : (
                        <CustomerSearchCard key={customer.id} customer={customer} />
                    )
                )}
            </>
        );
    }
    return (
        <>
            <div className="container-fluid searchPage">
                <div className="row">
                    {/*<!-- LEFT COL -->*/}
                    <CustomerSearchForm onSearch={onSearch} ref={formRef} setSearchReady={() => setSearchReady(true)}/>
                    {/*<!-- RIGHT COL -->*/}
                    <div className="col-lg-9 resultsCol">
                        {Object.keys(searchTerms).length > 0 && Object.values(searchTerms).some(inner => Object.keys(inner).length != 0) && (
                            <>
                                <div className="d-flex justify-content-between align-items-end mt-3">
                                    <span className="resultsFound my-3">
                                        {totalCount >= 0 ? `Found ${totalCount} results matching` : ''}
                                    </span>
                                    <div className="me-2 mb-4">
                                        <button onClick={() => setShowMessagingModal(true)} className="btn btn-outline-primary me-2 px-3">
                                            <i className="fa-regular fa-envelope"></i> Message
                                        </button>
                                        <button onClick={exportCustomers} className="btn btn-outline-primary px-3">
                                            <i className="fa-regular fa-file-export"></i> Export
                                        </button>
                                    </div>
                                </div>
        
                                <div className="searchTerms mb-3">
                                    {GetSearchTermsHTML()}
                                </div>
                                <div className="resultsList">
                                    {loading && <LoadingSpinner/>}
                                    {GetCustomerCards()}
                                </div>
                                {!loading && Object.keys(searchTerms).length > 0 && customers.length === 0  && (
                                    <div className="container-fluid pt-160 scanPage">
                                        <div className="messageDiv">
                                            <span className="thinky"><i className="fa-solid fa-face-thinking"></i></span>
                                            <span className="tldr">No customers found based on search results</span>
                                            <span className="message">Please try again with different search terms.</span>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        
                        {(Object.keys(searchTerms).length === 0 || Object.values(searchTerms).every(inner => Object.keys(inner).length === 0)) && (
                            <div className="container-fluid pt-160 scanPage">
                                <div className="messageDiv">
                                    <span className="thinky"><i className="fa-regular fa-circle-info"></i></span>
                                    <span className="message">Please use the form to search for customers</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <MessageCustomerModal searchTerms={searchTerms} show={showMessagingModal}
                                  toggle={() => setShowMessagingModal(false)} totalCustomerCount={totalCount}
                                  customers={customers}/>
        </>
    );
}

export default CustomerManagementPage